import { useMemo } from "react";
import { useFilterPositions } from "./useFilterTrades";
import { isPositionBalanceOperation } from "../historySummary";
import {
  computeAccountAnalytics,
  type AccountAnalytics,
  type AnalyticsScope,
} from "../accountAnalytics";

/**
 * useAccountAnalytics (proposal 70) — useTradeStatistics grown to the full
 * Myfxbook metric set, computed 100% client-side from the synced history
 * stores. Memoized; pass `enabled=false` while the modal is closed so the
 * heavy pass over large arrays never runs off-screen.
 *
 * Balance operations carry no symbol, so a symbol-scoped filter would drop
 * deposits; we pull them from an all-symbols scope with the same date window
 * (same predicate as useFilterBalanceOperations).
 */
export function useAccountAnalytics(
  scope: AnalyticsScope,
  enabled: boolean = true
): AccountAnalytics | null {
  const positions = useFilterPositions({
    symbol: scope.symbol,
    dateFilter: scope.dateFilter,
    customDateFrom: scope.customDateFrom,
    customDateTo: scope.customDateTo,
  });

  const allSymbolPositions = useFilterPositions({
    symbol: "allsymbols",
    dateFilter: scope.dateFilter,
    customDateFrom: scope.customDateFrom,
    customDateTo: scope.customDateTo,
  });

  return useMemo(() => {
    if (!enabled) return null;
    const balanceOperations =
      scope.symbol === "allsymbols"
        ? []
        : allSymbolPositions.filter(isPositionBalanceOperation);
    return computeAccountAnalytics({ positions, balanceOperations });
  }, [enabled, positions, allSymbolPositions, scope.symbol]);
}
